import { logger } from "@/lib/logger";
/**
 * UTM Parameter Tracking Utility
 * Captures and persists UTM parameters across the user journey
 */

export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
}

const UTM_STORAGE_KEY = 'registration_utm_params';
const UTM_TIMESTAMP_KEY = 'registration_utm_timestamp';
const UTM_EXPIRY_DAYS = 30;

/**
 * Extract UTM parameters from current URL
 */
export function extractUTMFromURL(): UTMParams {
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'),
    utm_term: urlParams.get('utm_term'),
  };
}

/**
 * Check if UTM parameters are expired (older than 30 days)
 */
function isUTMExpired(): boolean {
  const timestamp = localStorage.getItem(UTM_TIMESTAMP_KEY);
  if (!timestamp) return true;
  
  const savedDate = new Date(timestamp);
  const now = new Date();
  const daysDiff = (now.getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysDiff > UTM_EXPIRY_DAYS;
}

/**
 * Store UTM parameters in localStorage with timestamp
 */
export function persistUTMParams(params: UTMParams): void {
  // Only store if at least one UTM parameter exists
  if (Object.values(params).some(val => val !== null)) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    localStorage.setItem(UTM_TIMESTAMP_KEY, new Date().toISOString());
    logger.debug('[UTM Tracking] Captured and persisted:', params);
  }
}

/**
 * Retrieve stored UTM parameters from localStorage
 */
export function getStoredUTMParams(): UTMParams {
  // Check if expired
  if (isUTMExpired()) {
    clearUTMParams();
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
    };
  }

  const stored = localStorage.getItem(UTM_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      logger.error('[UTM Tracking] Failed to parse stored UTM params:', e);
    }
  }
  
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
  };
}

/**
 * Clear stored UTM parameters (called after successful registration)
 */
export function clearUTMParams(): void {
  localStorage.removeItem(UTM_STORAGE_KEY);
  localStorage.removeItem(UTM_TIMESTAMP_KEY);
  logger.debug('[UTM Tracking] Cleared stored UTM parameters');
}

/**
 * Main function: Capture UTM from URL and persist if present
 * Falls back to stored UTM if URL has no UTM params
 */
export function captureAndPersistUTM(): UTMParams {
  const urlUTM = extractUTMFromURL();
  
  // If URL has UTM params, use and persist them
  if (Object.values(urlUTM).some(val => val !== null)) {
    persistUTMParams(urlUTM);
    return urlUTM;
  }
  
  // Otherwise, return stored UTM params
  return getStoredUTMParams();
}

/**
 * Get UTM parameters for conversion tracking
 * This is the main function used by hooks and components
 */
export function getUTMForTracking(): UTMParams {
  // First check URL
  const urlUTM = extractUTMFromURL();
  if (Object.values(urlUTM).some(val => val !== null)) {
    return urlUTM;
  }
  
  // Fall back to stored
  return getStoredUTMParams();
}
